import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { syncAppOrganizations, type AdminRegistryApp, type OrganizationSyncError } from '@/api/console';
import { Button, FormField, Modal, Select } from '@/components/ui';
import { useRootOrganizationOptions } from '../accounts/shared/useOrgLevelPicker';
import { describeSyncError } from './syncErrorMessages';

export interface SyncOrganizationsModalProps {
    app: AdminRegistryApp | null;
    onClose: () => void;
    /** EAA-1878: fired after a successful run so the host page can invalidate
     *  whatever registry query might one day surface org counts on the app
     *  card — mirrors `AppDetailPage`'s own catalog-sync invalidation. */
    onSuccess?: () => void;
}

/**
 * EAA-1878 (WP-4, PLAN-lms-org-school-sync.md §3): "Đồng bộ tổ chức" — mirrors
 * `SyncUsersModal` file-for-file. Live pull of one app's own organization/
 * school tree (`POST /admin/apps/{code}/sync-organizations`), mirrored into
 * Portal `Organization` rows (root per LMS org, child per school).
 *
 * Result shape is one field richer than `SyncUsersModal`'s
 * (`created`/`linked`/`errors`): org-sync is mark-and-sweep — it can UPDATE
 * existing rows and DEACTIVATE ones no longer reported, not just create new
 * ones — so the stat row shows all four counts.
 *
 * No auto-close on success, matching `SyncUsersModal`'s own behaviour, so an
 * operator can read the result before dismissing.
 *
 * EAA-1887: an optional root-Organization picker sits above the run button —
 * every top-level synced entry is parented under the chosen root instead of
 * becoming its own new root (see `OrganizationSyncService`'s `root_org`
 * docstring). Left unset, the run behaves exactly as EAA-1878 shipped it.
 */
export function SyncOrganizationsModal({ app, onClose, onSuccess }: SyncOrganizationsModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const [rootOrganizationId, setRootOrganizationId] = useState<string | undefined>(undefined);
    const rootOrgs = useRootOrganizationOptions(Boolean(app));

    const syncMutation = useMutation({
        mutationFn: () =>
            syncAppOrganizations(
                config.apiBase,
                accessToken as string,
                (app as AdminRegistryApp).code,
                rootOrganizationId
            ),
        onSuccess: () => onSuccess?.(),
    });

    function handleClose() {
        if (syncMutation.isPending) return;
        setRootOrganizationId(undefined);
        onClose();
    }

    // EAA-1878: `OrganizationSyncService`'s per-row errors tag EITHER
    // `organization_id` (root-level failures) or `school_id` (child-row
    // failures), never both — render whichever key is present rather than
    // assuming a single shared identifier like `UserSyncError.external_id`.
    function describeErrorRow(row: OrganizationSyncError): string {
        if (row.organization_id !== undefined) return `organization_id=${row.organization_id}`;
        if (row.school_id !== undefined) return `school_id=${row.school_id}`;
        return '';
    }

    return (
        <Modal
            open={Boolean(app)}
            onCancel={handleClose}
            title={t('console.apps.syncOrganizationsModal.title', { name: app?.name })}
            width={520}
            footer={
                <>
                    <Button onClick={handleClose} disabled={syncMutation.isPending}>
                        {t('common.close')}
                    </Button>
                    <Button type="primary" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
                        {syncMutation.isPending
                            ? t('console.apps.syncOrganizationsModal.runningLabel')
                            : syncMutation.data
                              ? t('console.apps.syncOrganizationsModal.rerunButton')
                              : t('console.apps.syncOrganizationsModal.runButton')}
                    </Button>
                </>
            }
        >
            <div className="apps-sync-users">
                <p>
                    <Trans
                        i18nKey="console.apps.syncOrganizationsModal.description"
                        values={{ name: app?.name }}
                        components={{ bold: <strong /> }}
                    />
                </p>

                <FormField
                    label={t('console.apps.syncOrganizationsModal.rootOrgLabel')}
                    hint={t('console.apps.syncOrganizationsModal.rootOrgHint')}
                >
                    <Select
                        value={rootOrganizationId}
                        onChange={(next) => setRootOrganizationId((next as string) ?? undefined)}
                        options={rootOrgs.options}
                        loading={rootOrgs.loading}
                        disabled={syncMutation.isPending}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder={t('console.apps.syncOrganizationsModal.rootOrgPlaceholder')}
                        style={{ width: '100%' }}
                    />
                </FormField>

                {syncMutation.isError && (
                    <p className="portal-admin-error">{t('console.apps.syncOrganizationsModal.errorMessage')}</p>
                )}

                {syncMutation.data && (
                    <>
                        <div className="apps-sync-users__stats">
                            <div className="apps-sync-users__stat apps-sync-users__stat--created">
                                <span className="apps-sync-users__stat-value">{syncMutation.data.created}</span>
                                <span className="apps-sync-users__stat-label">
                                    {t('console.apps.syncOrganizationsModal.statCreated')}
                                </span>
                            </div>
                            <div className="apps-sync-users__stat apps-sync-users__stat--updated">
                                <span className="apps-sync-users__stat-value">{syncMutation.data.updated}</span>
                                <span className="apps-sync-users__stat-label">
                                    {t('console.apps.syncOrganizationsModal.statUpdated')}
                                </span>
                            </div>
                            <div className="apps-sync-users__stat apps-sync-users__stat--deactivated">
                                <span className="apps-sync-users__stat-value">
                                    {syncMutation.data.deactivated}
                                </span>
                                <span className="apps-sync-users__stat-label">
                                    {t('console.apps.syncOrganizationsModal.statDeactivated')}
                                </span>
                            </div>
                            <div className="apps-sync-users__stat apps-sync-users__stat--errors">
                                <span className="apps-sync-users__stat-value">
                                    {syncMutation.data.errors.length}
                                </span>
                                <span className="apps-sync-users__stat-label">
                                    {t('console.apps.syncOrganizationsModal.statErrors')}
                                </span>
                            </div>
                        </div>

                        {syncMutation.data.errors.length > 0 && (
                            <div className="apps-sync-users__error-box">
                                <span className="apps-sync-users__error-box-title">
                                    {t('console.apps.syncOrganizationsModal.errorRowsTitle', {
                                        count: syncMutation.data.errors.length,
                                    })}
                                </span>
                                <ul className="apps-sync-users__error-list">
                                    {syncMutation.data.errors.map((row, index) => {
                                        // EAA-1883: known `error_code` values render a friendly,
                                        // translated sentence; an absent/unrecognized code falls back
                                        // to the raw backend string under a "technical error" badge
                                        // rather than hiding it — see `syncErrorMessages.ts`.
                                        const display = describeSyncError(row, t);
                                        return (
                                            <li key={`${describeErrorRow(row)}-${index}`}>
                                                {describeErrorRow(row)} —{' '}
                                                {!display.isKnown && (
                                                    <span className="portal-console-badge is-warn apps-sync-users__error-badge">
                                                        {t('console.apps.syncErrors.unknownLabel')}
                                                    </span>
                                                )}
                                                {display.text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}

export default SyncOrganizationsModal;
